import lookupDataService from '@/services/data/LookupDataService';
import { InspectionModalData } from '@/types/model/inspection-modal';
import { Inspection } from '@/types/model/inspection';
import { FileResponse, InspectionLineItemResponse } from '@/types/response/InspectionResultResponses';

export const mapInspectionResponseToModalData = (
  inspectionResponse: InspectionLineItemResponse,
  filesResponse: FileResponse[],
  fallbackInspection?: Partial<Inspection>,
): InspectionModalData => {
  const inspectionResult = inspectionResponse.InspectionResults?.[0];

  if (!inspectionResult) {
    throw new Error('Inspection result not found.');
  }

  return {
    inspectionResultId: inspectionResult.Id,
    title: inspectionResponse.Title,
    inspectionDate: inspectionResult.InspectedOn || new Date().toISOString(),
    inspectorName: inspectionResult.UserId
      ? lookupDataService.getInspectorNameById(inspectionResult.UserId) || 'Unknown Inspector'
      : 'Unknown Inspector',
    siteId: inspectionResult.SiteId,
    userId: inspectionResult.UserId,
    lineItem: inspectionResponse.LineItem,
    lineItemId: inspectionResponse.Id,
    siteName: inspectionResult.SiteId
      ? lookupDataService.getSiteNameById(inspectionResult.SiteId) || 'Unknown Site'
      : 'Unknown Site',
    isExceptional: inspectionResult.IsExceptional,
    comments: inspectionResult.Comments,
    checklistItems: inspectionResult.ChecklistItems,
    correctiveActions: [],
    attachments: inspectionResult.Attachments?.map(att => ({
      Id: att.Id,
      Description: att.Description,
      FileId: att.FileId,
      Filename: att.Filename,
      Link: att.Link,
    })) ?? [],
    existingResources: filesResponse,
    isInProgress: inspectionResult.IsInProgress,
    isApproved: inspectionResult.IsApproved,
    approvalComments: inspectionResult.ApprovalComments,
    alertDays: inspectionResult.AlertDays,
    alertEmails: inspectionResult.AlertEmails ?? '',
    lineItemDetail: {
      MajorRequirement: inspectionResponse.MajorRequirement ?? null,
      RelatedInfo: inspectionResponse.RelatedInfo ?? null,
      PerformanceStandard: inspectionResponse.PerformanceStandard ?? null,
      EstimatedQuantity: inspectionResponse.EstimatedQuantity ?? null,
      LineItemAttachments: inspectionResponse.LineItemAttachments?.map(att => ({
        Id: att.Id,
        Description: att.Description,
        FileId: att.FileId,
        Filename: att.Filename,
        Link: att.Link,
      })) ?? [],
    },
    inspectionPlanBackground: {
      LineItemPrimaryWeight: inspectionResponse.PrimaryValue ?? null,
      LineItemAlternateWeight: inspectionResponse.AlternateValue ?? null,
      TimeToInspect: inspectionResponse.TimeToInspect ?? null,
      MethodOfInspection: inspectionResponse.InspectionMethodId
        ? lookupDataService.getInspectionMethodNameById(inspectionResponse.InspectionMethodId) ?? null
        : null,
      SampleSize: inspectionResponse.SampleSize ?? null,
      SampleSelection: inspectionResponse.SampleSelection ?? null,
      AdditionalInstructions: inspectionResponse.AdditionalInstructions ?? null,
      InspectionPlanAttachments: inspectionResponse.InspectionPlanAttachments?.map(att => ({
        Id: att.Id,
        Description: att.Description,
        FileId: att.FileId,
        Filename: att.Filename,
        Link: att.Link,
      })) ?? [],
    },
    timeSensitivity: fallbackInspection?.timeSensitivity ?? 'unknown',
    status: fallbackInspection?.status ?? 'unknown',
    scheduledDate: fallbackInspection?.scheduledDate ?? null,
  };
};
